import type { VideoOverlay, VideoAspectRatio, VideoAnimation } from '@/pages/video-editor/types';

interface ExportOptions {
  videoUrl: string;
  overlays: VideoOverlay[];
  aspectRatio: VideoAspectRatio;
  audio?: {
    url: string;
    startTime: number;
    trimStart: number;
    trimEnd: number;
    volume?: number;
  } | null;
  onProgress?: (progress: number) => void;
}

const ASPECT_DIMS: Record<VideoAspectRatio, { w: number; h: number }> = {
  '1:1': { w: 1080, h: 1080 },
  '9:16': { w: 1080, h: 1920 },
  '16:9': { w: 1920, h: 1080 },
};

function getAnimationOpacity(
  animation: VideoAnimation,
  startTime: number,
  currentTime: number,
  duration: number,
): number {
  const elapsed = currentTime - startTime;
  if (elapsed < 0 || elapsed > duration) return 0;
  const fadeIn = Math.min(elapsed / Math.min(0.5, duration * 0.3), 1);
  return fadeIn;
}

function drawOverlay(
  ctx: CanvasRenderingContext2D,
  overlay: VideoOverlay,
  canvasW: number,
  canvasH: number,
  currentTime: number,
  logoImages: Map<string, HTMLImageElement>,
): void {
  const opacity = getAnimationOpacity(overlay.animation, overlay.startTime, currentTime, overlay.duration);
  if (opacity <= 0) return;

  ctx.save();
  ctx.globalAlpha = overlay.opacity * opacity;

  const x = (overlay.x / 100) * canvasW;
  const y = (overlay.y / 100) * canvasH;
  const w = (overlay.width / 100) * canvasW;

  if (overlay.type === 'logo' && overlay.imageUrl) {
    const img = logoImages.get(overlay.id);
    if (img) {
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const h = w / aspectRatio;
      ctx.drawImage(img, x, y, w, h);
    }
  } else if (overlay.type === 'cta') {
    const fontSize = (overlay.fontSize / 1080) * canvasW;
    ctx.font = `${overlay.fontWeight} ${fontSize}px ${overlay.fontFamily}`;
    const textW = ctx.measureText(overlay.content).width;
    const padX = fontSize * 1.4;
    const padY = fontSize * 0.7;
    const btnW = textW + padX * 2;
    const btnH = fontSize + padY * 2;

    // Background
    ctx.fillStyle = overlay.backgroundColor;
    const r = (overlay.borderRadius / 100) * Math.min(btnW, btnH);
    ctx.beginPath();
    ctx.roundRect(x, y, btnW, btnH, r);
    ctx.fill();

    // Text
    ctx.fillStyle = overlay.color;
    ctx.textBaseline = 'middle';
    ctx.fillText(overlay.content, x + padX, y + btnH / 2);
  } else {
    // Text
    const fontSize = (overlay.fontSize / 1080) * canvasW;
    ctx.font = `${overlay.fontWeight} ${fontSize}px ${overlay.fontFamily}`;
    ctx.fillStyle = overlay.color;
    ctx.textBaseline = 'top';

    // Text shadow
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    // Word wrap
    const words = overlay.content.split(' ');
    let line = '';
    let lineY = y;
    const lineHeight = fontSize * 1.3;

    for (const word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > w && line !== '') {
        ctx.fillText(line.trim(), x, lineY);
        line = word + ' ';
        lineY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line.trim(), x, lineY);
  }

  ctx.restore();
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function exportVideoWithOverlays({
  videoUrl,
  overlays,
  aspectRatio,
  audio,
  onProgress,
}: ExportOptions): Promise<void> {
  const dims = ASPECT_DIMS[aspectRatio];

  // Load video
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.src = videoUrl;
  video.muted = true;
  video.playsInline = true;

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = reject;
    video.load();
  });

  const duration = video.duration;

  // Pre-load logo images
  const logoImages = new Map<string, HTMLImageElement>();
  for (const overlay of overlays) {
    if (overlay.type === 'logo' && overlay.imageUrl) {
      try {
        const img = await loadImage(overlay.imageUrl);
        logoImages.set(overlay.id, img);
      } catch { /* skip */ }
    }
  }

  // Setup canvas
  const canvas = document.createElement('canvas');
  canvas.width = dims.w;
  canvas.height = dims.h;
  const ctx = canvas.getContext('2d')!;

  const audioContext = audio ? new AudioContext() : null;
  let audioSource: AudioBufferSourceNode | null = null;
  let audioDestination: MediaStreamAudioDestinationNode | null = null;

  if (audio && audioContext) {
    const audioRes = await fetch(audio.url);
    if (!audioRes.ok) throw new Error('Could not load voiceover audio for export.');
    const audioBuffer = await audioContext.decodeAudioData(await audioRes.arrayBuffer());
    const trimStart = Math.max(0, Math.min(audio.trimStart, audioBuffer.duration));
    const requestedTrimEnd = audio.trimEnd > trimStart ? audio.trimEnd : audioBuffer.duration;
    const trimEnd = Math.max(trimStart, Math.min(requestedTrimEnd, audioBuffer.duration));
    const availableInVideo = Math.max(0, duration - Math.max(0, audio.startTime));
    const playbackDuration = Math.min(trimEnd - trimStart, availableInVideo);

    if (playbackDuration > 0) {
      audioDestination = audioContext.createMediaStreamDestination();
      const gain = audioContext.createGain();
      gain.gain.value = audio.volume ?? 1;
      audioSource = audioContext.createBufferSource();
      audioSource.buffer = audioBuffer;
      audioSource.connect(gain).connect(audioDestination);
    }
  }

  // Setup MediaRecorder
  const stream = canvas.captureStream(30);
  if (audioDestination) {
    audioDestination.stream.getAudioTracks().forEach(track => stream.addTrack(track));
  }
  const preferredMimeType = audioDestination ? 'video/webm;codecs=vp9,opus' : 'video/webm;codecs=vp9';
  const mimeType = MediaRecorder.isTypeSupported(preferredMimeType)
    ? preferredMimeType
    : MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
    ? 'video/webm;codecs=vp9'
    : 'video/webm';

  const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

  const recordingDone = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });

  video.currentTime = 0;
  await new Promise<void>((r) => { video.onseeked = () => r(); });

  recorder.start(100);
  await audioContext?.resume();
  if (audio && audioContext && audioSource) {
    const audioBuffer = audioSource.buffer;
    const trimStart = Math.max(0, Math.min(audio.trimStart, audioBuffer?.duration ?? 0));
    const requestedTrimEnd = audio.trimEnd > trimStart ? audio.trimEnd : audioBuffer?.duration ?? trimStart;
    const trimEnd = Math.max(trimStart, Math.min(requestedTrimEnd, audioBuffer?.duration ?? trimStart));
    const availableInVideo = Math.max(0, duration - Math.max(0, audio.startTime));
    const playbackDuration = Math.min(trimEnd - trimStart, availableInVideo);
    audioSource.start(audioContext.currentTime + Math.max(0, audio.startTime), trimStart, playbackDuration);
  }

  await video.play();
  await new Promise<void>((resolve) => {
    const startedAt = performance.now();
    const maxExportMs = Math.max(duration * 1000 + 1500, 2500);

    const render = () => {
      const t = Math.min(video.currentTime, duration);
      ctx.drawImage(video, 0, 0, dims.w, dims.h);

      for (const overlay of overlays) {
        drawOverlay(ctx, overlay, dims.w, dims.h, t, logoImages);
      }

      onProgress?.(duration > 0 ? Math.min(t / duration, 1) : 1);

      if (video.ended || t >= duration || performance.now() - startedAt > maxExportMs) {
        video.pause();
        resolve();
        return;
      }

      requestAnimationFrame(render);
    };

    requestAnimationFrame(render);
  });

  recorder.stop();
  await recordingDone;
  await audioContext?.close();

  // Download
  const blob = new Blob(chunks, { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `adforge-video-edited-${Date.now()}.webm`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
